/**
 * Generic VERB-shape `AuditEvent` mapper (PROD-642 Sprint 2.2).
 *
 * Captures the canonical CPS audit shape: subject (who acted), verb
 * (what was done), object (the primary target), and indirect_object
 * (the secondary target, optional). Per `docs/03-domain-model.mdx`
 * (vault) every actor-verb-object event in the prototype lands as a
 * FHIR `AuditEvent` so the audit trail is one resource per fact.
 *
 * The mapper round-trips losslessly: any `VerbAuditPayload` produced by
 * `auditPayloadToFhir` reconstructs identically via `fhirToAuditPayload`.
 *
 * HC9 strict registry: the `source` discriminator is required; an
 * AuditEvent cannot be built without naming the originating site.
 *
 * HC5: zero em / en dashes anywhere.
 */
import type {
  AuditEvent,
  AuditEventEntity,
  AuditEventAgent,
  Coding,
  FhirExtension,
} from '../fhir/types';
import { TENANT_EXTENSION_URL } from '../fhir/types';

const NS = 'http://scrips.com/fhir/StructureDefinition/cps';
export const AUDIT_VERB_SYSTEM = `${NS}-audit-verb`;
export const AUDIT_OBJECT_TYPE_SYSTEM = `${NS}-audit-object-type`;
export const AUDIT_INDIRECT_OBJECT_TYPE_SYSTEM = `${NS}-audit-indirect-object-type`;
export const AUDIT_INDIRECT_OBJECT_ROLE_CODE = 'indirect-object';
export const AUDIT_DIRECT_OBJECT_ROLE_CODE = 'direct-object';

/**
 * Recipient extension URL (Sprint 4.3.3, PROD-652) per Plan v9 §5.3.3
 * AuditEvent VERB shape contract:
 *   - `AuditEvent.agent[]`           <- subject
 *   - `AuditEvent.action`            <- action verb code (C/R/U/D/E)
 *   - `AuditEvent.entity[]`          <- object
 *   - `AuditEvent.recipient` (ext)   <- indirect_object
 *
 * Distinct from the legacy entity[].role discriminator. The recipient
 * extension carries a structured target (reference + display + type
 * code) so a downstream consumer can reconstruct the indirect_object
 * even if the entity[] array is filtered by role-aware processors.
 *
 * The Sprint 4.3.3 contract preserves the existing entity[]-based
 * encoding (role = `indirect-object`) so consumers that already filter
 * on `entity.role.code` keep working; the recipient extension is the
 * canonical surface going forward.
 */
export const AUDIT_EVENT_RECIPIENT_EXTENSION_URL =
  'http://scrips.com/fhir/StructureDefinition/audit-event-recipient';

/** A single AuditEvent target (object or indirect object). */
export interface VerbAuditTarget {
  /**
   * FHIR-compatible reference, e.g. `urn:cps:nba:tlp1-nba-1` or
   * `Patient/sara-r`. Required.
   */
  reference: string;
  /** Free-form display label for human-readable audit trails. */
  display?: string;
  /** Optional categorical type code (CPS vocabulary). */
  typeCode?: string;
}

export interface VerbAuditPayload {
  /** ISO timestamp the event was recorded. Required. */
  recorded: string;
  /** Required `source` discriminator per HC9 strict registry. */
  source: string;
  /** Tenant id; lands on the `tenant-id` extension. */
  tenantId: string;
  /** Subject who performed the action (FHIR reference). */
  subject: VerbAuditTarget;
  /** Verb code (e.g. `confirmed`, `dispatched`, `overrode`). */
  verb: string;
  /** Optional human-friendly verb display. */
  verbDisplay?: string;
  /** Direct object of the verb. */
  object: VerbAuditTarget;
  /** Optional indirect object (e.g. `to: <patient>`, `for: <pathway>`). */
  indirectObject?: VerbAuditTarget;
  /**
   * Optional FHIR `AuditEvent.action` C/R/U/D/E. Defaults to `E`
   * (execute) for verb-shape events that do not match CRUD semantics.
   */
  action?: 'C' | 'R' | 'U' | 'D' | 'E';
  /** Optional outcome flag (FHIR `0` success, `4` minor, `8` serious, `12` major). */
  outcome?: '0' | '4' | '8' | '12';
}

function toEntity(
  target: VerbAuditTarget,
  roleCode: string,
  typeSystem: string,
): AuditEventEntity {
  const role: Coding = {
    system: `${NS}-audit-role`,
    code: roleCode,
  };
  const type: Coding | undefined = target.typeCode
    ? { system: typeSystem, code: target.typeCode }
    : undefined;
  return {
    what: { reference: target.reference, display: target.display },
    role,
    type,
    name: target.display,
  };
}

function fromEntity(entity: AuditEventEntity): VerbAuditTarget {
  const reference = entity.what?.reference;
  if (!reference) {
    throw new Error('AuditEvent.entity missing what.reference');
  }
  return {
    reference,
    display: entity.what?.display ?? entity.name,
    typeCode: entity.type?.code,
  };
}

/** Build the FHIR `AuditEvent` from a verb-shape payload. */
export function auditPayloadToFhir(payload: VerbAuditPayload): AuditEvent {
  if (!payload.source) {
    throw new Error(
      'auditPayloadToFhir: source discriminator is required (HC9 strict registry)',
    );
  }
  const verbCoding: Coding = {
    system: AUDIT_VERB_SYSTEM,
    code: payload.verb,
    display: payload.verbDisplay,
  };
  const agents: AuditEventAgent[] = [
    {
      who: {
        reference: payload.subject.reference,
        display: payload.subject.display,
      },
      requestor: true,
      type: payload.subject.typeCode
        ? {
            coding: [
              {
                system: `${NS}-audit-subject-type`,
                code: payload.subject.typeCode,
              },
            ],
          }
        : undefined,
    },
  ];
  const entities: AuditEventEntity[] = [
    toEntity(payload.object, AUDIT_DIRECT_OBJECT_ROLE_CODE, AUDIT_OBJECT_TYPE_SYSTEM),
  ];
  if (payload.indirectObject) {
    entities.push(
      toEntity(
        payload.indirectObject,
        AUDIT_INDIRECT_OBJECT_ROLE_CODE,
        AUDIT_INDIRECT_OBJECT_TYPE_SYSTEM,
      ),
    );
  }
  const extension: FhirExtension[] = [
    { url: TENANT_EXTENSION_URL, valueString: payload.tenantId },
  ];
  if (payload.indirectObject) {
    // Plan v9 §5.3.3: indirect_object lands on the `recipient` extension
    // in addition to the legacy `entity[].role = indirect-object` row.
    // Nested-extension payload preserves reference + display + typeCode.
    const recipientChildExt: FhirExtension[] = [
      {
        url: 'reference',
        valueReference: { reference: payload.indirectObject.reference },
      },
    ];
    if (payload.indirectObject.display !== undefined) {
      recipientChildExt.push({
        url: 'display',
        valueString: payload.indirectObject.display,
      });
    }
    if (payload.indirectObject.typeCode !== undefined) {
      recipientChildExt.push({
        url: 'typeCode',
        valueCode: payload.indirectObject.typeCode,
      });
    }
    extension.push({
      url: AUDIT_EVENT_RECIPIENT_EXTENSION_URL,
      extension: recipientChildExt,
    });
  }
  return {
    resourceType: 'AuditEvent',
    type: verbCoding,
    action: payload.action ?? 'E',
    recorded: payload.recorded,
    outcome: payload.outcome ?? '0',
    agent: agents,
    source: {
      observer: { reference: `Device/${payload.source}` },
      site: payload.source,
    },
    entity: entities,
    extension,
  };
}

/** Inverse: parse a FHIR `AuditEvent` back into the verb-shape payload. */
export function fhirToAuditPayload(resource: AuditEvent): VerbAuditPayload {
  if (resource.resourceType !== 'AuditEvent') {
    throw new Error(
      `fhirToAuditPayload: expected AuditEvent, got ${resource.resourceType}`,
    );
  }
  const verb = resource.type.code;
  if (!verb) {
    throw new Error('AuditEvent.type missing verb code');
  }
  const tenantExt = resource.extension?.find(
    (e) => e.url === TENANT_EXTENSION_URL,
  );
  const tenantId = tenantExt?.valueString;
  if (!tenantId) {
    throw new Error('AuditEvent missing tenant-id extension');
  }
  const source = resource.source.site;
  if (!source) {
    throw new Error('AuditEvent.source.site missing (HC9 strict registry)');
  }
  const subjectAgent = resource.agent[0];
  if (!subjectAgent?.who?.reference) {
    throw new Error('AuditEvent.agent[0] missing who.reference');
  }
  const subject: VerbAuditTarget = {
    reference: subjectAgent.who.reference,
    display: subjectAgent.who.display,
    typeCode: subjectAgent.type?.coding?.[0]?.code,
  };
  const entities = resource.entity ?? [];
  const directEntity = entities.find(
    (e) => e.role?.code === AUDIT_DIRECT_OBJECT_ROLE_CODE,
  );
  if (!directEntity) {
    throw new Error('AuditEvent missing direct-object entity');
  }
  const indirectEntity = entities.find(
    (e) => e.role?.code === AUDIT_INDIRECT_OBJECT_ROLE_CODE,
  );
  // Plan v9 §5.3.3: prefer the recipient extension when present so the
  // canonical VERB-shape contract drives the round-trip; fall back to
  // the legacy entity[].role row for backwards compatibility.
  const recipientExt = resource.extension?.find(
    (e) => e.url === AUDIT_EVENT_RECIPIENT_EXTENSION_URL,
  );
  let indirectObject: VerbAuditTarget | undefined;
  if (recipientExt?.extension) {
    const ref = recipientExt.extension.find((e) => e.url === 'reference')
      ?.valueReference?.reference;
    if (!ref) {
      throw new Error(
        'AuditEvent recipient extension missing nested reference value',
      );
    }
    const disp = recipientExt.extension.find((e) => e.url === 'display')
      ?.valueString;
    const typeCode = recipientExt.extension.find((e) => e.url === 'typeCode')
      ?.valueCode;
    indirectObject = { reference: ref };
    if (disp !== undefined) indirectObject.display = disp;
    if (typeCode !== undefined) indirectObject.typeCode = typeCode;
  } else if (indirectEntity) {
    indirectObject = fromEntity(indirectEntity);
  }
  return {
    recorded: resource.recorded,
    source,
    tenantId,
    subject,
    verb,
    verbDisplay: resource.type.display,
    object: fromEntity(directEntity),
    indirectObject,
    action: resource.action,
    outcome: resource.outcome,
  };
}
